import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { TrendingUp, BarChart3, AlertTriangle } from 'lucide-react';
import { monthlyAnalysisAPI } from '../services/api';
import { DEFAULT_REPORT_MONTH, DEFAULT_REPORT_YEAR } from '../utils/reportPeriod';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/**
 * Monthly analysis trend chart, scoped to a single dimension.
 * - type="subscriber": subscriber growth line chart
 * - type="revenue": monthly revenue by service type bar chart
 */
const MonthlyTrends = ({ month, year, type = 'subscriber' }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const monthParam = month != null ? month : DEFAULT_REPORT_MONTH;
  const yearParam = year != null ? year : DEFAULT_REPORT_YEAR;
  const isRevenue = type === 'revenue';

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const periodParams = { month: monthParam, year: yearParam };
        const res = isRevenue
          ? await monthlyAnalysisAPI.getRevenueTrends(periodParams)
          : await monthlyAnalysisAPI.getSubscriberTrends(periodParams);
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error('Error fetching monthly trends:', err);
        if (!cancelled) setError(t('monthlyTrends.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when period/type changes
  }, [monthParam, yearParam, isRevenue]);

  // Build labels (subscriber endpoint may return labels or months + years)
  const subscriberLabels = data
    ? (data.labels && data.labels.length > 0)
      ? data.labels
      : (data.months || []).map((m, i) => {
          const str = String(m);
          if (/\b20\d{2}\b/.test(str)) return str;
          const y = (data.years && data.years[i]) ?? data.year ?? yearParam;
          return `${str} ${y}`;
        })
    : [];

  const subscriberChartData = data ? {
    labels: subscriberLabels,
    datasets: [
      {
        label: t('monthlyTrends.totalSubscribers'),
        data: data.total_subscribers,
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#3b82f6',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 5,
      },
      {
        label: t('monthlyTrends.activeSubscribers'),
        data: data.active_subscribers,
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#10b981',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 5,
      },
    ],
  } : null;

  const revenueChartData = data ? {
    labels: data.months,
    datasets: [
      {
        label: t('monthlyTrends.mbbRevenue'),
        data: data.mbb_revenue,
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: '#3b82f6',
        borderWidth: 1,
      },
      {
        label: t('monthlyTrends.ftbRevenue'),
        data: data.ftb_revenue,
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: '#10b981',
        borderWidth: 1,
      },
    ],
  } : null;

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true, padding: 20 } },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString()}`,
        },
      },
    },
    scales: {
      x: { display: true, title: { display: true, text: t('monthlyTrends.month') } },
      y: {
        display: true,
        title: { display: true, text: t('monthlyTrends.subscribers') },
        ticks: { callback: (value) => (value / 1000000).toFixed(1) + 'M' },
      },
    },
    interaction: { mode: 'nearest', axis: 'x', intersect: false },
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true, padding: 20 } },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${Number(ctx.parsed.y).toLocaleString()}M ₭`,
        },
      },
    },
    scales: {
      x: { display: true, title: { display: true, text: t('monthlyTrends.month') } },
      y: {
        display: true,
        title: { display: true, text: t('monthlyTrends.revenueMillions') },
        ticks: { callback: (value) => Number(value).toLocaleString() + 'M ₭' },
      },
    },
  };

  const title = isRevenue ? t('monthlyTrends.revenueTitle') : t('monthlyTrends.subscriberTitle');
  const subtitle = isRevenue ? t('monthlyTrends.revenueSubtitle') : t('monthlyTrends.subscriberSubtitle');
  const Icon = isRevenue ? BarChart3 : TrendingUp;
  const iconColor = isRevenue ? 'text-green-500' : 'text-blue-500';

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Icon className={`h-5 w-5 mr-2 ${iconColor}`} />
          <div>
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">{subtitle}</p>
          </div>
        </div>
      </div>

      <div className="h-80">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <span>{error}</span>
            </div>
          </div>
        ) : isRevenue ? (
          revenueChartData ? <Bar data={revenueChartData} options={barChartOptions} /> : null
        ) : (
          subscriberChartData ? <Line data={subscriberChartData} options={lineChartOptions} /> : null
        )}
      </div>
    </div>
  );
};

export default MonthlyTrends;
